/**
 * Auth Storage Utility for PWA
 * Uses IndexedDB for secure token storage with fallback to localStorage
 * Provides better security and storage capacity for PWA
 */

const DB_NAME = 'mosqify_auth_db';
const DB_VERSION = 2; // Incremented to force schema recreation
const STORE_NAME = 'auth_tokens';
const USER_STORE_NAME = 'user_data';

let dbInstance = null;

/**
 * Initialize IndexedDB database
 */
const initDB = () => {
  return new Promise((resolve, reject) => {
    if (dbInstance) {
      resolve(dbInstance);
      return;
    }

    // Check if IndexedDB is available
    if (typeof indexedDB === 'undefined') {
      console.warn('[AuthStorage] IndexedDB not available, using localStorage only');
      resolve(null);
      return;
    }

    try {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = (event) => {
        console.warn('[AuthStorage] IndexedDB error:', event.target.error);
        resolve(null);
      };

      request.onsuccess = (event) => {
        dbInstance = event.target.result;
        
        // Handle connection closing unexpectedly
        dbInstance.onversionchange = () => {
          dbInstance.close();
          dbInstance = null;
        };
        
        resolve(dbInstance);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Delete old stores if they exist (clean upgrade)
        if (db.objectStoreNames.contains(STORE_NAME)) {
          db.deleteObjectStore(STORE_NAME);
        }
        if (db.objectStoreNames.contains(USER_STORE_NAME)) {
          db.deleteObjectStore(USER_STORE_NAME);
        }

        // Create object store for tokens
        const tokenStore = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
        tokenStore.createIndex('expiresAt', 'expiresAt', { unique: false });

        // Create object store for user data
        db.createObjectStore(USER_STORE_NAME, { keyPath: 'key' });
        
        console.log('[AuthStorage] Database schema upgraded to version', DB_VERSION);
      };
      
      request.onblocked = () => {
        console.warn('[AuthStorage] Database blocked - close other tabs and reload');
        resolve(null);
      };
    } catch (error) {
      console.warn('[AuthStorage] Error opening IndexedDB:', error);
      resolve(null);
    }
  });
};

/**
 * Get value from IndexedDB
 */
const getFromIndexedDB = async (key) => {
  try {
    const db = await initDB();
    if (!db) return null;

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(key);

      request.onsuccess = () => {
        resolve(request.result?.value || null);
      };

      request.onerror = () => {
        resolve(null);
      };
    });
  } catch (error) {
    console.warn('[AuthStorage] Error reading from IndexedDB:', error);
    return null;
  }
};

/**
 * Set value in IndexedDB
 */
const setInIndexedDB = async (key, value, expiresAt = null) => {
  try {
    const db = await initDB();
    if (!db) return false;

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put({ key, value, expiresAt, updatedAt: Date.now() });

      request.onsuccess = () => {
        resolve(true);
      };

      request.onerror = () => {
        resolve(false);
      };
    });
  } catch (error) {
    console.warn('[AuthStorage] Error writing to IndexedDB:', error);
    return false;
  }
};

/**
 * Remove value from IndexedDB
 */
const removeFromIndexedDB = async (key) => {
  try {
    const db = await initDB();
    if (!db) return false;

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(key);

      request.onsuccess = () => {
        resolve(true);
      };

      request.onerror = () => {
        resolve(false);
      };
    });
  } catch (error) {
    console.warn('[AuthStorage] Error removing from IndexedDB:', error);
    return false;
  }
};

/**
 * Clear all auth data from IndexedDB
 */
const clearIndexedDB = async () => {
  try {
    const db = await initDB();
    if (!db) return false;

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME, USER_STORE_NAME], 'readwrite');
      
      const tokenStore = transaction.objectStore(STORE_NAME);
      const userStore = transaction.objectStore(USER_STORE_NAME);

      tokenStore.clear();
      userStore.clear();

      transaction.oncomplete = () => {
        resolve(true);
      };

      transaction.onerror = () => {
        resolve(false);
      };
    });
  } catch (error) {
    console.warn('[AuthStorage] Error clearing IndexedDB:', error);
    return false;
  }
};

/**
 * Clean expired tokens from IndexedDB
 */
const cleanExpiredTokens = async () => {
  try {
    const db = await initDB();
    if (!db) return;

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('expiresAt');
      const now = Date.now();

      const request = index.openCursor(IDBKeyRange.upperBound(now));

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          resolve();
        }
      };

      request.onerror = () => {
        resolve();
      };
    });
  } catch (error) {
    console.warn('[AuthStorage] Error cleaning expired tokens:', error);
  }
};

/**
 * Auth Storage API with IndexedDB + localStorage fallback
 */
export const authStorage = {
  /**
   * Get token from storage
   */
  async getToken() {
    // Try IndexedDB first
    const token = await getFromIndexedDB('access_token');
    if (token) return token;

    // Fallback to localStorage
    return localStorage.getItem('mosqify_token');
  },

  /**
   * Set token in storage
   */
  async setToken(token, expiresIn = null) {
    // Calculate expiration time
    const expiresAt = expiresIn ? Date.now() + (expiresIn * 1000) : null;

    // Try IndexedDB first
    const success = await setInIndexedDB('access_token', token, expiresAt);
    if (success) {
      // Also store in localStorage as fallback
      localStorage.setItem('mosqify_token', token);
      if (expiresAt) {
        localStorage.setItem('mosqify_token_expires', expiresAt.toString());
      }
      return true;
    }

    // Fallback to localStorage
    localStorage.setItem('mosqify_token', token);
    if (expiresAt) {
      localStorage.setItem('mosqify_token_expires', expiresAt.toString());
    }
    return true;
  },

  /**
   * Remove token from storage
   */
  async removeToken() {
    await removeFromIndexedDB('access_token');
    localStorage.removeItem('mosqify_token');
    localStorage.removeItem('mosqify_token_expires');
  },

  /**
   * Get token expiration time
   */
  async getTokenExpiration() {
    try {
      const db = await initDB();
      if (db) {
        const result = await getFromIndexedDB('access_token');
        if (result && result.expiresAt) {
          return result.expiresAt;
        }
      }

      // Fallback to localStorage
      const expiresStr = localStorage.getItem('mosqify_token_expires');
      return expiresStr ? parseInt(expiresStr, 10) : null;
    } catch (error) {
      const expiresStr = localStorage.getItem('mosqify_token_expires');
      return expiresStr ? parseInt(expiresStr, 10) : null;
    }
  },

  /**
   * Check if token is expired or will expire soon
   */
  async isTokenExpiredOrExpiringSoon(bufferSeconds = 300) {
    const expiresAt = await this.getTokenExpiration();
    if (!expiresAt) {
      // No expiration info, check if token exists
      const token = await this.getToken();
      return !token; // Treat as expired if no token
    }

    const now = Date.now();
    const bufferMs = bufferSeconds * 1000;
    return expiresAt <= (now + bufferMs);
  },

  /**
   * Get remaining time until token expiration (in seconds)
   */
  async getTokenTimeRemaining() {
    const expiresAt = await this.getTokenExpiration();
    if (!expiresAt) return null;

    const now = Date.now();
    const remaining = Math.floor((expiresAt - now) / 1000);
    
    return remaining > 0 ? remaining : 0;
  },

  /**
   * Get user data from storage
   */
  async getUser() {
    try {
      // Try IndexedDB first
      const db = await initDB();
      if (db) {
        return new Promise((resolve) => {
          try {
            const transaction = db.transaction([USER_STORE_NAME], 'readonly');
            const store = transaction.objectStore(USER_STORE_NAME);
            const request = store.get('user_data');

            request.onsuccess = () => {
              if (request.result?.value) {
                resolve(request.result.value);
              } else {
                // Fallback to localStorage
                const userStr = localStorage.getItem('mosqify_user');
                resolve(userStr ? JSON.parse(userStr) : null);
              }
            };

            request.onerror = () => {
              const userStr = localStorage.getItem('mosqify_user');
              resolve(userStr ? JSON.parse(userStr) : null);
            };
          } catch (e) {
            const userStr = localStorage.getItem('mosqify_user');
            resolve(userStr ? JSON.parse(userStr) : null);
          }
        });
      }

      // Fallback to localStorage
      const userStr = localStorage.getItem('mosqify_user');
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      console.warn('[AuthStorage] Error getting user:', error);
      const userStr = localStorage.getItem('mosqify_user');
      return userStr ? JSON.parse(userStr) : null;
    }
  },

  /**
   * Set user data in storage
   */
  async setUser(user) {
    try {
      // Always store in localStorage as primary fallback
      localStorage.setItem('mosqify_user', JSON.stringify(user));
      
      const db = await initDB();
      if (db) {
        return new Promise((resolve) => {
          try {
            const transaction = db.transaction([USER_STORE_NAME], 'readwrite');
            const store = transaction.objectStore(USER_STORE_NAME);
            store.put({ key: 'user_data', value: user, updatedAt: Date.now() });
            
            transaction.oncomplete = () => resolve(true);
            transaction.onerror = () => resolve(true); // Still true since localStorage worked
          } catch (e) {
            resolve(true); // localStorage already set
          }
        });
      }
      
      return true;
    } catch (error) {
      console.warn('[AuthStorage] Error setting user:', error);
      localStorage.setItem('mosqify_user', JSON.stringify(user));
      return true;
    }
  },

  /**
   * Remove user data from storage
   */
  async removeUser() {
    await removeFromIndexedDB('user_data');
    localStorage.removeItem('mosqify_user');
  },

  /**
   * Clear all auth data
   */
  async clear() {
    await clearIndexedDB();
    localStorage.removeItem('mosqify_token');
    localStorage.removeItem('mosqify_token_expires');
    localStorage.removeItem('mosqify_user');
  },

  /**
   * Initialize and clean expired tokens
   */
  async init() {
    await initDB();
    await cleanExpiredTokens();
  },
};

// Initialize on module load
if (typeof window !== 'undefined') {
  authStorage.init();
  
  // Sync auth token with service worker when it changes
  let lastToken = null;
  const syncWithServiceWorker = async () => {
    const currentToken = await authStorage.getToken();
    if (currentToken !== lastToken) {
      lastToken = currentToken;
      const expiresAt = await authStorage.getTokenExpiration();
      
      // Import dynamically to avoid circular dependency
      import('./serviceWorkerAuth').then(({ syncAuthWithServiceWorker }) => {
        syncAuthWithServiceWorker(currentToken, expiresAt);
      });
    }
  };

  // Sync on token change
  const originalSetToken = authStorage.setToken;
  authStorage.setToken = async function(token, expiresIn) {
    const result = await originalSetToken.call(this, token, expiresIn);
    await syncWithServiceWorker();
    return result;
  };

  const originalRemoveToken = authStorage.removeToken;
  authStorage.removeToken = async function() {
    await originalRemoveToken.call(this);
    lastToken = null;
    await syncWithServiceWorker();
  };
}

